import { z } from "zod";
import { useRef } from "react";

export function useMixpanel() {
  // NOTE: this mockMixpanel is used in case no valid mixpanel was found
  // - so the type stay the same and we don't need to do `mixpanel?.track()` each time (talking 'bout the '?.')
  // - so in case the real one is not valid we can have a fallback. (currently nothing)
  // - so we have a debug 'fake' mixpanle in local dev
  const mockMixpanel = {
    track(event: string, payload: Record<string, any>) {
      console.debug("WARNING, a valid mixpanel not found on window, 'track'.", { event, payload });
    },
    identify(userId: string) {
      console.debug("WARNING, a valid mixpanel not found on window, 'identify'.", { userId });
    },
    people: {
      set(profile: Record<any, any>) {
        console.debug("WARNING, a valid mixpanel not found on window, 'people.set'.", { profile });
      }
    },
  } satisfies Mixpanel;

  // NOTE: mockMixpanel is for local dev, if you see it in real env it's a problem
  const mixpanel = window.mixpanel as Mixpanel || mockMixpanel;

  const mixpanel_data = window.mixpanel_data;

  // Use refs to track whether identify and people.set have been called
  const hasIdentified = useRef(false);
  const hasSetProfile = useRef(false);

  if (mixpanel_data?.identify && !hasIdentified.current) {
    mixpanel.identify(mixpanel_data.identify);
    hasIdentified.current = true;
  }

  if (mixpanel_data?.profile && !hasSetProfile.current) {
    mixpanel.people.set(mixpanel_data.profile);
    hasSetProfile.current = true;
  }

  const originalTrack = mixpanel.track.bind(mixpanel);
  mixpanel.track = (event, payload) => originalTrack(event, {
    ...payload,
    ...(window.session_id ? { session_id: window.session_id } : {}),
  });

  return mixpanel as typeof mockMixpanel; // we use 'as' so we get the names of the args
}

const mixpanelSchema = z.object({
  identify: z.function().args(z.string()),
  track: z.function().args(z.string(), z.record(z.string(), z.any())),
  people: z.object({
    set: z.function().args(z.record(z.any(), z.any())),
  }),
});
type Mixpanel = z.infer<typeof mixpanelSchema>;

type PHPNullObject = never[];
type MixpanelData = {
  events?: Record<string, any> | PHPNullObject; // This is not used by us
  identify?: string; // To be passed to the mixpanel.identify() function
  profile?: Record<string, any> | PHPNullObject; // To be passed to mixpanel.people.set() function
}

declare global {
  interface Window {
    mixpanel?: Mixpanel;
    mixpanel_data?: MixpanelData;
    session_id?: string;
  }
}
